import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Building2, Handshake } from "lucide-react";

import { loginSearchSchema } from "@/api/contracts/auth-error-codes";
import type { AuthErrorCode } from "@/lib/auth-error-codes";
import type { ProviderId } from "@/lib/identity";

const ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  oauth_error: "Sign-in was cancelled or failed. Please try again.",
  state_invalid: "Your sign-in session expired before you returned. Please start sign-in again.",
  provider_mismatch:
    "Your sign-in flow used a different identity provider than the one that returned. Please start sign-in again.",
  unknown_provider: "That identity provider is not configured. Please choose Internal or External.",
  exchange_failed:
    "We couldn't complete sign-in with your identity provider. Please try again in a moment.",
  storage_unavailable:
    "Sign-in succeeded with your identity provider, but we could not save your session. Our database may be temporarily unavailable — please try again in a moment.",
};

export const Route = createFileRoute("/login")({
  // Zod v4 implements Standard Schema, so the schema passes in directly.
  validateSearch: loginSearchSchema,
  head: () => ({ meta: [{ title: "Sign in" }] }),
  component: Login,
});

function Login() {
  const { redirect, error } = Route.useSearch();

  function hrefFor(provider: ProviderId): string {
    const base = `/api/auth/login/${provider}`;
    return redirect ? `${base}?redirect=${encodeURIComponent(redirect)}` : base;
  }

  return (
    <div className="bg-lennar-grey-50 flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="border-lennar-grey-200 w-full max-w-md space-y-6 rounded-xl border bg-white p-8 shadow-sm">
        <div>
          <div className="flex justify-center">
            <img src="/lennar-logo.svg" alt="Lennar" className="h-6 w-auto" />
          </div>
          <h2 className="text-lennar-blue-600 mt-6 text-center text-3xl font-medium tracking-tight">
            Sign in to the Lennar Platform
          </h2>
          <p className="text-lennar-grey-600 mt-2 text-center text-base font-light">Spec Tool</p>
        </div>

        {error && (
          <div
            role="alert"
            className="flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <p>{ERROR_MESSAGES[error]}</p>
          </div>
        )}

        <div className="space-y-3">
          <a
            href={hrefFor("entra")}
            className="group border-lennar-blue-200 bg-lennar-blue-100 hover:bg-lennar-blue-200 hover:border-lennar-blue-400 focus:ring-lennar-blue-500 flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border px-4 py-3 transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            <div className="flex items-center gap-3">
              <div className="bg-lennar-blue-600 flex h-9 w-9 items-center justify-center rounded-md text-white">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="text-left">
                <div className="text-lennar-blue-600 text-sm font-semibold">Internal Lennar</div>
                <div className="text-lennar-grey-500 text-xs">Sign in with Microsoft Entra</div>
              </div>
            </div>
            <span
              aria-hidden
              className="text-lennar-blue-500 transition-transform group-hover:translate-x-0.5"
            >
              →
            </span>
          </a>

          <a
            href={hrefFor("pingid")}
            className="group border-lennar-teal-200 bg-lennar-teal-50 hover:bg-lennar-teal-100 hover:border-lennar-teal-400 focus:ring-lennar-teal-500 flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border px-4 py-3 transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            <div className="flex items-center gap-3">
              <div className="bg-lennar-teal-500 flex h-9 w-9 items-center justify-center rounded-md text-white">
                <Handshake className="h-5 w-5" />
              </div>
              <div className="text-left">
                <div className="text-lennar-teal-600 text-sm font-semibold">
                  External Trade Partner
                </div>
                <div className="text-lennar-grey-500 text-xs">Sign in with PingID</div>
              </div>
            </div>
            <span
              aria-hidden
              className="text-lennar-teal-500 transition-transform group-hover:translate-x-0.5"
            >
              →
            </span>
          </a>
        </div>

        <p className="text-lennar-grey-500 text-center text-xs">
          Lennar employees: choose <strong className="text-lennar-grey-600">Internal Lennar</strong>
          . Trade partners and external collaborators: choose{" "}
          <strong className="text-lennar-grey-600">External Trade Partner</strong>.
        </p>
      </div>
    </div>
  );
}
